export type ThermostatSortColumn = "name" | "status" | "properties" | "provider" | "account";
export interface ThermostatListRow {
  deviceId: string; customName: string | null; seamName: string;
  propertyId: number | null; propertyIds: number[]; online: boolean | null;
  provider: string; accountEmail: string | null; issueReasons?: string[];
}
export function filterThermostatRows<T extends ThermostatListRow>(
  rows: T[], propertyId: number | null, status: "all" | "offline" | "online", attention: boolean,
): T[] {
  return rows.filter((d) => (propertyId == null || d.propertyId === propertyId || d.propertyIds.includes(propertyId)) &&
    (status === "all" || (status === "online" ? d.online === true : d.online === false)) &&
    (!attention || (d.issueReasons?.length ?? 0) > 0));
}
export function thermostatRowComparator<T extends ThermostatListRow>(
  column: ThermostatSortColumn, direction: "asc" | "desc",
  propertyName: (id: number) => string, providerLabel: (provider: string) => string,
): (a: T, b: T) => number {
  const name = (d: T) => (d.customName || d.seamName || "Unnamed device").trim();
  const properties = (d: T) => [...new Set([...(d.propertyId == null ? [] : [d.propertyId]), ...d.propertyIds])]
    .map(propertyName).sort((a, b) => a.localeCompare(b)).join(", ");
  const values: Record<ThermostatSortColumn, (d: T) => string> = {
    name, status: (d) => d.online == null ? "Unknown" : d.online ? "Online" : "Offline",
    properties, provider: (d) => providerLabel(d.provider), account: (d) => d.accountEmail || "",
  };
  const compare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
  return (a, b) => {
    const left = values[column](a).trim(), right = values[column](b).trim();
    // Missing labels stay last in both directions; ties do not flip by direction.
    if (!left !== !right) return left ? -1 : 1;
    return (direction === "asc" ? 1 : -1) * compare(left, right) ||
      compare(name(a), name(b)) || a.deviceId.localeCompare(b.deviceId);
  };
}
